import Phaser from "phaser";
import { SpineGameObject } from "@esotericsoftware/spine-phaser-v4";
import { AvatarSystem } from "@/game/systems/avatar";
import { AudioSystem, preloadAudioAssets } from "@/game/systems/audio";
import { GameRuntime } from "@/game/runtime";
import { useGameStore } from "@/shared/state/gameStore";
import { assetUrl } from "@/shared/utils/assets";

export default class StreamScene extends Phaser.Scene {
  hero: SpineGameObject | null;
  private avatar!: AvatarSystem;
  private audioSystem!: AudioSystem;
  private runtime!: GameRuntime;
  private unsubscribeStore?: () => void;
  private unsubscribeTap?: () => void;
  private unsubscribeAnimation?: () => void;

  constructor() {
    super({ key: "StreamScene" });
    this.hero = null;
  }

  init(data: { runtime: GameRuntime }) {
    this.runtime = data.runtime;
  }

  preload(): void {
    this.load.spineJson("man", assetUrl("spine/man/skeleton.json"));
    this.load.spineAtlas(
      "manAtlas",
      assetUrl("spine/man/skeleton.atlas"),
      true,
    );
    preloadAudioAssets(this);
  }

  private createSpineObject(
    x: number,
    y: number,
    dataKey: string,
    atlasKey: string,
  ): SpineGameObject {
    const addAny = this.add as any;

    if (addAny.spine) {
      return addAny.spine(x, y, dataKey, atlasKey) as SpineGameObject;
    }

    const spineObj = new SpineGameObject(
      this,
      (this as any).spine,
      x,
      y,
      dataKey,
      atlasKey,
    );

    this.add.existing(spineObj);
    return spineObj;
  }

  create(): void {
    const { width, height } = this.scale;

    const seamY = height * 0.86;
    const sideWallWidth = width * 0.14;

    this.cameras.main.setBackgroundColor(0xebebeb);

    // Back wall
    this.add
      .rectangle(width * 0.5, seamY * 0.5, width, seamY, 0xe2e2e2)
      .setDepth(0);

    // Side walls
    this.add
      .rectangle(
        sideWallWidth * 0.5,
        seamY * 0.5,
        sideWallWidth,
        seamY,
        0xd8d8d8,
      )
      .setDepth(1);

    this.add
      .rectangle(
        width - sideWallWidth * 0.5,
        seamY * 0.5,
        sideWallWidth,
        seamY,
        0xd8d8d8,
      )
      .setDepth(1);

    // Floor
    this.add
      .rectangle(width * 0.5, height, width, height - seamY, 0xcfcfcf)
      .setOrigin(0.5, 1)
      .setDepth(2);

    // Seam
    this.add.rectangle(width * 0.5, seamY, width, 3, 0x000000, 0.1).setDepth(3);

    // Room corner lines
    this.add
      .rectangle(sideWallWidth, seamY * 0.5, 2, seamY, 0x000000, 0.05)
      .setDepth(3);

    this.add
      .rectangle(width - sideWallWidth, seamY * 0.5, 2, seamY, 0x000000, 0.05)
      .setDepth(3);

    // Avatar
    this.avatar = new AvatarSystem(this, this.createSpineObject.bind(this));
    this.audioSystem = new AudioSystem(this, this.runtime);
    this.audioSystem.start();
    this.hero = this.avatar.spawn(width * 0.5, seamY + 50);

    // Initial sync from runtime
    this.avatar.applyState(useGameStore.getState().avatarState);

    // Persistent avatar state sync
    this.unsubscribeStore = useGameStore.subscribe((state) => {
      this.avatar.applyState(state.avatarState);
    });

    // Temporary avatar reactions
    this.unsubscribeTap = this.runtime.on("avatarReaction", (event) => {
      switch (event.reaction) {
        case "tap":
          this.avatar.playTapReaction();
          break;

        case "heart":
          this.avatar.showTemporarySlot("heart", "skin/expression/heart", 500);
          break;

        case "celebrate":
          this.avatar.showTemporarySlot(
            "exclamation",
            "skin/expression/exmalation",
            400,
          );
          break;

        case "embarrassed":
          this.avatar.showTemporarySlot(
            "embarrassed",
            "skin/expression/embarrassed",
            700,
          );
          this.avatar.showTemporarySlot("blush", "skin/expression/blush", 700);
          break;
      }
    });

    this.unsubscribeAnimation = this.runtime.on("animationPlayed", (event) => {
      this.avatar.playAnimation(event.animationId);
    });
  }

  shutdown(): void {
    this.unsubscribeStore?.();
    this.unsubscribeTap?.();
    this.unsubscribeAnimation?.();
    this.audioSystem?.destroy();
  }

  update(_time: number, delta: number) {
    const dt = Math.min(delta / 1000, 0.1);
    this.runtime.tick(dt);
  }
}
